import {
    Button,
    Pane,
    Paragraph,
    TextInputField,
    TrashIcon,
} from "evergreen-ui";
import { ChangeEvent } from "react";
import { StoreTeamTab } from "./StoreTeamTab";
import {
    ContentPosition,
    DetailsPageSection,
} from "../../common/DetailsPageSection";
import { useStoreEditDetailsContext } from "./StoreEditDetailsContext";
import { PrefixInputField } from "../../common/PrefixInputField";
import { FooterActionBar } from "../../common/FooterActionBar";
import { CenteredSpinner } from "../../common/CenteredSpinner";
import { LockIcon } from "evergreen-ui";
import { ColorPalette } from "../../context/Theme";
import { useGetStorefrontQuery } from "../../datastore/TundraStorefrontDataStore";
import { useState } from "react";
import { DeleteStoreDialog } from "./etc/DeleteStoreDialog";

export const StoreEditDetailsTab = (): JSX.Element => {
    const {
        store,
        formValues,
        isFieldValid,
        setField,
        isFormValid,
        isFormDirty,
        formStatusMessage,
        resetForm,
        submitFormLoading,
        submitFormCalled,
        submitForm,
    } = useStoreEditDetailsContext();

    const { data: getStorefrontData, loading: getStorefrontLoading } =
        useGetStorefrontQuery(store?.domain ?? "");

    const [deleteDialogIsShown, setDeleteDialogIsShown] =
        useState<boolean>(false);

    const handleStoreNameChange = (e: ChangeEvent<HTMLInputElement>): void => {
        setField("storeName", e.target.value);
    };

    if (getStorefrontLoading) {
        return <CenteredSpinner />;
    }

    const storeId = store?.domain || getStorefrontData?.storefront.storeId || "";

    const storeCreatedAt = new Date(store?.createdAt ?? Date.now());
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
    const isStoreRecentlyCreated = storeCreatedAt > fiveMinutesAgo;

    return (
        <Pane>
            {/* Store Details Section */}
            <DetailsPageSection
                heading={
                    <div className="jc-header-wrapper">
                        <h3 className="jc-header-title">Details</h3>
                    </div>
                }
                contentPosition={ContentPosition.left}
            >
                <br />
                {/* This className is required to override the colors of the labels in Evergreen UI components */}
                <div className="jc-create-store-custom-colors">
                    <TextInputField
                        name={"storeName"}
                        label={"Store Name"}
                        isInvalid={!isFieldValid("storeName")}
                        maxWidth={"620px"}
                        value={formValues.storeName}
                        className="jc-create-store-input"
                        onChange={handleStoreNameChange}
                    />
                    {/* Legacy Storefronts that have been deleted will not show the Prefix Input Field */}
                    {getStorefrontData && (
                        <PrefixInputField
                            name={"storefront-id"}
                            label={"Store ID"}
                            maxWidth={"620px"}
                            disabled={true}
                            suffix={<LockIcon size={12} color={ColorPalette.lightGreyText} />}
                            value={storeId}
                            className="jc-create-store-input"
                        />
                    )}
                </div>
            </DetailsPageSection>

            {/* Store Team Section */}
            <StoreTeamTab />

            {/* Delete Section */}
            {/* If Store created within 5 mins from Date.now(), render the delete section for all admin users to allow reverting a mistake */}
            {/* TODO - Need a new INTERNAL-ADMIN rampart group under which users will always have the ability to delete a store */}
            {isStoreRecentlyCreated && (
                <DetailsPageSection
                    heading={
                        <div className="jc-header-wrapper">
                            <h3 className="jc-header-title">Delete</h3>
                        </div>
                    }
                    hideBorder={true}
                    contentPosition={ContentPosition.left}
                >
                    {deleteDialogIsShown && (
                        <DeleteStoreDialog
                            showDialog={deleteDialogIsShown}
                            setShowDialog={setDeleteDialogIsShown}
                        />
                    )}
                    <Paragraph
                        lineHeight={"22px"}
                        fontSize={13}
                        fontWeight={400}
                        marginTop={15}
                    >
                        A store can only be deleted if it has no associated data (payout
                        rules, product listings, etc.). Otherwise, contact Engineering for
                        manual database deletion. If you only want to take down the public
                        website, edit the Visibility settings in Domains.
                    </Paragraph>

                    <Button
                        iconBefore={<TrashIcon size={12} />}
                        size={"medium"}
                        height={40}
                        minWidth={150}
                        borderRadius={10}
                        marginTop={24}
                        marginBottom={60}
                        style={{
                            backgroundColor: ColorPalette.lightGreyText,
                            color: ColorPalette.white,
                        }}
                        onClick={() => setDeleteDialogIsShown(true)}
                    >
                        Delete
                    </Button>
                </DetailsPageSection>
            )}

            {/* Footer Action Bar only shows if changes are made to the Store Name Input*/}
            {isFormDirty && (
                <FooterActionBar
                    primaryButtonText={"Save"}
                    isPrimaryLoading={submitFormCalled && submitFormLoading}
                    primaryButtonDisabled={
                        !isFormValid || (submitFormCalled && submitFormLoading)
                    }
                    onPrimaryClick={submitForm}
                    showSecondaryButton={false}
                    tertiaryButtonText={"Discard Changes"}
                    onTertiaryClick={resetForm}
                    infoComponent={<p>{formStatusMessage}</p>}
                />
            )}
        </Pane>
    );
};
